import {
  CreatorProduct,
  AllProductTypes,
} from '@services/MarppleShopStudio/widget/CreatorProduct/core/zod-schema/types.ts';
import { MODE } from '@services/MarppleShopStudio/widget/CreatorProduct/core/types/types.ts';
import { base64ToFile, isBase64DataUrl, urlToFile } from '@services/MarppleShop/shared/util/front.ts';
import { each, pipe, toAsync, filter, map, entries } from '@fxts/core';
import axios from 'axios';
import { makeApiUrl } from '@services/MarppleShop/shared/util';
import { upload } from '@services/MarppleShop/shared/client/global';
import { PRODUCT_STORY_MAX_WIDTH } from '@modules/Creator/ProductStoryEditor/S/width';
import { ValidateFormErrorKeyTypes } from '@services/MarppleShopStudio/widget/CreatorProduct/core/store/helper.ts';
import { getResizedUrl } from '@services/MarppleShop/shared/util/image.ts';

interface EditorSaveOptions {
  body?: Record<string, any>;
}

export interface EditorApiError {
  title: string | null;
  message: string | null;
}

export class EditorSaveClientError extends Error {
  constructor(public keys: Array<ValidateFormErrorKeyTypes>) {
    super();

    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, EditorSaveClientError);
    }
  }

  static is<T extends EditorSaveClientError>(this: new (...args: any[]) => T, e: unknown): e is T {
    return e instanceof this;
  }
}

export const safeErrorParse = (error: any, onError?: (error: Error) => void): Partial<EditorApiError> => {
  try {
    return error.response && JSON.parse(error.response);
  } catch (_error) {
    onError?.(_error as Error);
    return { title: null, message: null };
  }
};

/**
 * 레거시 API 데이터 구조 호환한 formData 리턴
 * @see modules/MShop/Studio/PbProductCreator/F/Function/fn.js
 */
export const createCommonFormData = async (
  data: CreatorProduct<typeof MODE.NEW>,
  options?: EditorSaveOptions,
): Promise<FormData> => {
  const formData = new FormData();
  if (options?.body)
    pipe(
      entries(options.body),
      each(([key, value]) =>
        Array.isArray(value) ? value.forEach((v) => formData.append(key, v)) : formData.append(key, value),
      ),
    );

  const not_remove_product_story_image_files: number[] = [];
  const product_story_image_no: { id?: number; no: number }[] = [];

  await pipe(
    data._.product._.product_story._.product_story_images,
    toAsync,
    filter((product_story_image) => {
      if (product_story_image.id) not_remove_product_story_image_files.push(product_story_image.id);
      product_story_image_no.push({ id: data?.id ? product_story_image.id : 0, no: product_story_image.no });
      return data?.id ? !product_story_image.id : true;
    }),
    map(async (product_story_image) =>
      isBase64DataUrl(product_story_image.url)
        ? await base64ToFile(
            product_story_image.url,
            `${product_story_image.id}_${product_story_image.created_at}`,
          )
        : await urlToFile(
            getResizedUrl({
              url: product_story_image.url,
              width: 912,
              format: 'png',
            }),
            `${product_story_image.id}_${product_story_image.created_at}`,
          ),
    ),
    each((file) => formData.append('product_story_images', file)),
  );

  data._.product.store_id = data.store_id;
  data._.product._.product_story._.product_story_images = [];

  formData.append('product_story', JSON.stringify(data._.product._.product_story));
  formData.append('product_story_image_no', JSON.stringify(product_story_image_no));
  formData.append(
    'not_remove_product_story_image_files',
    JSON.stringify(not_remove_product_story_image_files),
  );
  formData.append('membership_ids', JSON.stringify(data.membership_ids));
  formData.append('stores_product', JSON.stringify(data));

  return formData;
};

export const createApi = async (data: CreatorProduct<typeof MODE.NEW>, options?: EditorSaveOptions) => {
  const formData = await createCommonFormData(data, options);
  return upload(formData, {
    method: 'POST',
    url: makeApiUrl(`/@api/stores/:store_id/pb_products/create`, {
      store_id: data.store_id,
    }),
    max_resize: PRODUCT_STORY_MAX_WIDTH,
  });
};

export const tempSaveApi = async (data: CreatorProduct<typeof MODE.NEW>, options?: EditorSaveOptions) => {
  const formData = await createCommonFormData(data, options);
  return upload(formData, {
    method: 'POST',
    url: makeApiUrl(`/@api/stores/:store_id/pb_products/temp_save`, {
      store_id: data.store_id,
    }),
    max_resize: PRODUCT_STORY_MAX_WIDTH,
  });
};

export const editApi = async (data: CreatorProduct<typeof MODE.EDIT>, options?: EditorSaveOptions) => {
  const formData = await createCommonFormData(data, options);
  return upload(formData, {
    method: 'PUT',
    url: makeApiUrl('/@api/stores/:store_id/pb_products/:stores_product_id/update', {
      store_id: data.store_id,
      stores_product_id: data.id,
    }),
    max_resize: PRODUCT_STORY_MAX_WIDTH,
  });
};

export const editTplApi = async (data: CreatorProduct<typeof MODE.EDIT_TPL>, options?: EditorSaveOptions) => {
  const formData = await createCommonFormData(data, options);
  return upload(formData, {
    method: 'PUT',
    url: makeApiUrl('/@api/stores/:store_id/pb_products/:stores_product_id/update', {
      store_id: data.store_id,
      stores_product_id: data.id,
    }),
    max_resize: PRODUCT_STORY_MAX_WIDTH,
  });
};

export const editTplIntlApi = async (
  data: CreatorProduct<typeof MODE.EDIT_TPL_INTL>,
  options?: EditorSaveOptions,
) => {
  const formData = await createCommonFormData(data, options);
  return upload(formData, {
    method: 'PUT',
    url: makeApiUrl('/@api/stores/:store_id/pb_products/:stores_product_id/update', {
      store_id: data.store_id,
      stores_product_id: data.id,
    }),
    max_resize: PRODUCT_STORY_MAX_WIDTH,
  });
};

export const saveApi = (mode: MODE, data: AllProductTypes, options?: EditorSaveOptions) => {
  switch (mode) {
    case MODE.NEW:
      return createApi(data, options);
    case MODE.EDIT:
      return editApi(data, options);
    case MODE.EDIT_TPL:
      return editTplApi(data, options);
    case MODE.EDIT_TPL_INTL:
      return editTplIntlApi(data, options);
  }
};

export const deleteApi = (store_id: number, product_id: number) => {
  return axios.delete(
    makeApiUrl(`/@api/stores/:store_id/products/:id`, {
      store_id,
      id: product_id,
    }),
  );
};

export const duplicateApi = (store_id: number, store_product_id: number) => {
  return axios.post(
    makeApiUrl(`/@api/stores/:store_id/pb_products/:stores_product_id/duplicate`, {
      store_id,
      stores_product_id: store_product_id,
    }),
  );
};

export const prohibitApi = (store_id: number, store_product_id: number, is_prohibited: boolean) => {
  return axios.patch(
    makeApiUrl('/@api/stores/:store_id/store_products/:id', {
      store_id,
      id: store_product_id,
    }),
    {
      is_prohibited,
    },
  );
};

export const toTplApi = (store_id: number, product_id: number, fee: number) => {
  return axios.post(
    makeApiUrl(`/@api/stores/:store_id/pb_products/:id/tpl`, {
      store_id,
      id: product_id,
    }),
    {
      tpl_fee: fee,
    },
  );
};

export const existTempApi = async (store_id: number) => {
  const {
    data: { data: store_product },
  } = await axios.get(
    makeApiUrl(`/@api/stores/:store_id/pb_products/temp_storage`, {
      store_id,
    }),
  );

  return store_product;
};
